import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLoading } from "../../context/LoadingContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { api } from "../../api/apiClient";

import "../../css/layout.css";
import "../../css/cards.css";
import "../../css/forms.css";
import "../../css/buttons.css";
import "../../css/login.css";

const Login = () => {
  const { login } = useAuth();
  const { setLoading } = useLoading();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const history = useHistory();

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await login(email, password);

      if (result.success) {
        history.push("/dashboard");
      } else {
        setError(result.error ?? "Login failed.");
      }
    } catch (e) {
      console.error("Error in handleLogin:", e);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    try {
      setError("");
      await api("/api/auth/forgot-password", {
        method: "POST",
        auth: "none",
        body: { email: email.trim() },
      });
      setResetSent(true);
    } catch {
      setError("Unable to request a password reset. Please try again.");
    }
  };

  const showForgotPassword = () => {
    setForgotPassword(true);
    setResetSent(false);
    setError("");
  };

  const showLogin = () => {
    setForgotPassword(false);
    setResetSent(false);
    setError("");
  };

  return (
    <div className="page-container login-page">
      <div className="card login-card">
        <div className="card-header">
          <div className="card-title">
            {forgotPassword ? "Reset your password" : "Sign In"}
          </div>
        </div>

        {forgotPassword && (
          <p className="login-intro">
            Enter your email address and we&apos;ll send you a password reset link.
          </p>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Email</label>
            <input
              className="form-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
        </div>

        {!forgotPassword && (
          <div className="form-row">
            <div className="form-group">
              <div className="login-password-header">
                <label>Password</label>
                <button
                  type="button"
                  className="link-button login-forgot-link"
                  onClick={showForgotPassword}
                >
                  Forgot your password?
                </button>
              </div>
              <div className="password-input-wrapper">
                <input
                  className="form-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <VisibilityOffIcon className="icon-green" />
                  ) : (
                    <VisibilityIcon className="icon-green" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="login-actions">
          <button
            type="button"
            className="form-submit-button"
            onClick={forgotPassword ? handleForgotPassword : handleLogin}
            disabled={forgotPassword && resetSent}
          >
            {forgotPassword ? "Send reset link" : "Login"}
          </button>
        </div>

        {error && <p className="error">{error}</p>}
        {resetSent && (
          <p className="login-status-message">
            If an account exists, password reset instructions have been emailed.
          </p>
        )}

        {forgotPassword && (
          <div className="login-secondary-action">
            <button
              type="button"
              className="link-button"
              onClick={showLogin}
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
